import { H2 } from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { Alert } from "../../../components/alert/Alert";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { BUSINESS } from "../../../constants/routes";
import brewAppIcon from "../../../images/apps/brew-app-icon.png";
import brewmanAppIcon from "../../../images/apps/brewman-app-icon.png";
import freeAgentAppIcon from "../../../images/apps/freeagent-app-icon.png";
import quickbooksAppIcon from "../../../images/apps/quickbooks-app-icon.png";
import viewplanAppIcon from "../../../images/apps/viewplan-app-icon.png";
import xeroAppIcon from "../../../images/apps/xero-app-icon.svg";
import { IntegrationApp } from "./IntegrationApp";
import { IntegrationPartner } from "./IntegrationPartner";
import { RyftIntegrationSettings } from "./RyftIntegrationSettings";
import { SellarIntegrationSettings } from "./SellarIntegrationSettings";
import { StripeIntegrationSettings } from "./StripeIntegrationSettings";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { RyftNonHostedOnboardingSettings } from "./RyftNonHostedOnboardingSettings";

const INVENTORY_PARTNERS = [
  {
    appName: "Breww",
    appImage: brewAppIcon,
    description: "Sync your stock levels, orders and customer list",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546384-breww-integration",
  },
  {
    appName: "Brewman",
    appImage: brewmanAppIcon,
    description: "Sync your stock levels, orders and customer list",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546386-brewman-integration",
  },
  {
    appName: "Viewplan",
    appImage: viewplanAppIcon,
    description: "Sync your stock levels, orders and customer list",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546387-viewplan-integration",
  },
] as const;

const ACCOUNTANCY_INTEGRATIONS = [
  {
    appName: "Xero",
    integrationType: "xero",
    appImage: xeroAppIcon,
    description: "Automatically create invoices in Xero from orders on Sellar",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546341-xero-integration",
  },
  {
    appName: "Quickbooks",
    integrationType: "quickbooks",
    appImage: quickbooksAppIcon,
    description:
      "Automatically create invoices in Quickbooks from orders on Sellar",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546347-quickbooks-integration",
  },
  {
    appName: "FreeAgent",
    integrationType: "freeagent",
    appImage: freeAgentAppIcon,
    description:
      "Automatically create invoices in FreeAgent from orders on Sellar",
    guideUrl:
      "https://intercom.help/sellario/en/collections/10546378-freeagent-integration",
  },
] as const;

export const IntegrationSettings = (): ReactElement => {
  const [message, setMessage] = useState<string>();
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const ryftNonHostedOnboardingEnabled = useRemoteFeatureFlags().isActive(
    "ryftNonHostedOnboarding",
  );

  return (
    <>
      <NavigationBar
        title={"Integrations"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(BUSINESS)}
      />
      <PageContainer className="flex flex-col gap-8 p-6">
        <section className="flex flex-col gap-4">
          <H2>Inventory management</H2>
          {message && <Alert type={"error"} message={message} />}
          <SellarIntegrationSettings setMessage={setMessage} />
          <div className="grid gap-4 md:grid-cols-2">
            {INVENTORY_PARTNERS.map((partner) => (
              <IntegrationPartner key={partner.appName} {...partner} />
            ))}
          </div>
        </section>
        <section className="flex flex-col gap-4">
          <H2>Accountancy</H2>
          <div className="grid gap-4 md:grid-cols-2">
            {ACCOUNTANCY_INTEGRATIONS.map((integration) => (
              <IntegrationApp
                key={integration.integrationType}
                setMessage={setMessage}
                {...integration}
              />
            ))}
          </div>
        </section>
        <section className="flex flex-col gap-4">
          <H2>Payments</H2>
          <div className="grid gap-4 md:grid-cols-2">
            <StripeIntegrationSettings setMessage={setMessage} />
            {!ryftNonHostedOnboardingEnabled && <RyftIntegrationSettings />}
            {ryftNonHostedOnboardingEnabled && (
              <RyftNonHostedOnboardingSettings />
            )}
          </div>
        </section>
      </PageContainer>
    </>
  );
};
